import { Component, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';
import { FilterDefinitionItem } from '../../../core/model/filter-definition-item';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { CommandFactoryService } from '../../../core/service/factory/command-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { leaseAgreementCommand } from '../../command/lease-agreement.command';
import { PremisesContract } from '../../model/premises-contract';
import { ContractService } from '../../service/base/contract';
import { ApproveContractCommandHandler } from '../../service/command/approve-contact-command-handler.service';
import { ContractCommandHandlerResolver } from '../../service/command/contract-command-handler-resolver.service';
import { ESignKeyRecieptCommandHandler } from '../../service/command/esign-key-reciept-document-command-handler.service';
import { ESignLeaseAgreementContractCommandHandler } from '../../service/command/esign-lease-agreement-document-command-handler.service';
import { PremisesContractCommandFactory } from '../../service/command/factory/premises-contract-command-factory.service';
import { PrintContractCommandHandler } from '../../service/command/print-contract-command-handler.service';
import { ShowLeaseAgreementInvoicesCommandHandler } from '../../service/command/show-lease-agreement-invoices-command-handler';
import { TerminateContractCommandHandler } from '../../service/command/terminate-contract-command-handler.service';
import { VoidContractCommandHandler } from '../../service/command/void-contract-command-handler.service';
import { WithdrawApprovalCommandHandler } from '../../service/command/withdraw-approval-command-handler.service';
import { WithdrawTerminationCommandHandler } from '../../service/command/withdraw-termination-command-handler.service';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { PremisesContractService } from '../../service/data/premises-contract.service';
import { ContractManagerPagedBase } from '../base/contract-manager-paged';
import { CustomPropertyService } from '../../service/data/custom-property.service';
import { CustomPropertyVisibilityType } from '../../model/const/enums';
import { CustomPropertyHelper } from '../../helper/customproperties-helper';
import { PropertyService } from '../../service/data/property.service';
import { CompanyService } from '../../service/data/company.service';
import { PremisesLeaseAgreementListService } from '../../service/data/lease-agreement-list.service';

@Component({
    selector: 'app-premises-contract-manager',
    templateUrl: 'premises-contract-manager.component.html',
    styleUrls: ['premises-contract-manager.component.scss', './../../../core/component/object-manager/object-manager.template.scss'],
    providers: [
        ApproveContractCommandHandler,
        ContractCommandHandlerResolver,
        ShowLeaseAgreementInvoicesCommandHandler,
        TerminateContractCommandHandler,
        VoidContractCommandHandler,
        ESignLeaseAgreementContractCommandHandler,
        ESignKeyRecieptCommandHandler,
        WithdrawApprovalCommandHandler,
        WithdrawTerminationCommandHandler,
        PrintContractCommandHandler,
        PremisesLeaseAgreementListService,
        { provide: ContractService, useExisting: PremisesContractService },
        { provide: CommandFactoryService, useClass: PremisesContractCommandFactory },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PremisesContractManagerComponent extends ContractManagerPagedBase<PremisesContract> {
  constructor(
    activatedRoute: ActivatedRoute,
    columnDefinitionService: ColumnDefinitionService,
    dispatcher: CommandDispatcher,
    handlerResolver: ContractCommandHandlerResolver,
    service: PremisesContractService,
    errorHandler: MessageService,
    userAccount: UserAccountService,
    navigationService: NavigationService,
    private readonly _customPropertyService: CustomPropertyService,
    private readonly _ch: CustomPropertyHelper,
    private readonly companyService: CompanyService,
    private readonly propertyService: PropertyService,
    leaseAgreementListService: PremisesLeaseAgreementListService
  ) {
    super(
      service,
      dispatcher,
      handlerResolver,
      errorHandler,
      navigationService,
      userAccount,
      companyService,
      propertyService,
      activatedRoute,
      leaseAgreementListService
    );

    this.columnDefinition = columnDefinitionService.create('PremisesContract');
    this.title = 'LEASE_AGREEMENT.PREMISES_LIST_HEADER';

    this.AddSubscription(
      userAccount.current.subscribe({
        next: account => {
          if (account.hasAccess(ModuleNames.Messages, Features.ManageESignatures)) {
            this.addContextMenuItemDefinition('COMMON.COMMAND.ESIGN_KEY_RECIEPT', leaseAgreementCommand.esignKeyReciept);
          }
          this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.PRINT', leaseAgreementCommand.print);
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.VOID', leaseAgreementCommand.void);
          }
          this.addListMenuItem('COMMON.EXCEL_EXPORT', leaseAgreementCommand.exportNoPersonalData);
          this.addListMenuItem('COMMON.COMMAND.EXPORT_EXCEL_WITH_PERSONAL_DATA', leaseAgreementCommand.export);
        },
      })
    );
  }
  override filterInternal(filterState: any): void {
    this._ch.applyCustomPropertyFilters(filterState);
  }

  addFilterDefinitions(definition: any) {
    this._customPropertyService.getList().subscribe(customProperties => {
      super.addFilterDefinitions(definition);
      this.filterDefinition.push(
        new FilterDefinitionItem(
          'LEASE_AGREEMENT.POPULAR_NAME',
          'popularName',
          definition.popularName,
          null,
          definition.selectedFields.includes('popularName')
        )
      );

      const customPropertiesFilters = this._ch.generateCustomPropertyFilters(
        customProperties,
        definition,
        CustomPropertyVisibilityType.Company |
          CustomPropertyVisibilityType.Property |
          CustomPropertyVisibilityType.Building |
          CustomPropertyVisibilityType.Premises
      );
      this._ch.createCustomPropertyColumnDefinition(
        customProperties,
        this.columnDefinition,
        CustomPropertyVisibilityType.Company |
          CustomPropertyVisibilityType.Property |
          CustomPropertyVisibilityType.Building |
          CustomPropertyVisibilityType.Premises
      );
      this.filterDefinition = [...this.filterDefinition, ...customPropertiesFilters];
    });
  }
}
